import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { loadMockAuth, isMockAuthAvailable } from '../../auth/mock-auth-loader';
import type { MockAuthUser } from '../../auth/mock-auth';
import { safeReturnTo } from '../../auth/safe-return-to';
import { getRuntimeConfig } from '../../config/runtime-config';

export function LoginScreen() {
  const { login, signInMockRole, authMode, isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const cfg = getRuntimeConfig();
  const [mockUsers, setMockUsers] = useState<MockAuthUser[]>([]);

  useEffect(() => {
    if (isAuthenticated) {
      window.location.assign(returnTo);
    }
  }, [isAuthenticated, returnTo]);

  // Lazy-load mock-auth only when the operator opens the mock-role
  // picker. In production builds this branch never runs because
  // `runtime-config.json` will have `authMode=zitadel`.
  useEffect(() => {
    if (authMode !== 'mock') return;
    if (!isMockAuthAvailable()) return;
    let cancelled = false;
    void loadMockAuth().then((m) => {
      if (cancelled) return;
      setMockUsers(m.l() as MockAuthUser[]);
    });
    return () => {
      cancelled = true;
    };
  }, [authMode]);

  const users = useMemo(() => mockUsers, [mockUsers]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          background: 'var(--nx-card)',
          border: '1px solid var(--nx-border)',
          borderRadius: 'var(--nx-r)',
          padding: 28,
        }}
      >
        <h1 style={{ fontSize: 28, margin: 0 }}>
          {/* Same external wordmark link as the SideNav brand. The
              gradient-text styles live on the anchor so the global
              `a { color: var(--nx-blue) }` rule cannot repaint it,
              and `textDecoration: 'none'` inline outranks the global
              hover/focus underline. */}
          <a
            href="https://nexsoss.ai"
            target="_blank"
            rel="noreferrer"
            aria-label="NEXSOSS — nexsoss.ai"
            style={{
              fontFamily: 'var(--nx-ff-h)',
              background: 'var(--nx-grad)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              letterSpacing: '0.18em',
              textDecoration: 'none',
            }}
          >
            NEXSOSS
          </a>
        </h1>
        <p style={{ color: 'var(--nx-muted)', marginTop: 8 }}>
          {authMode === 'mock'
            ? 'Local mock authentication — pick a role to sign in.'
            : `Sign in with ${cfg.zitadel.issuer}`}
        </p>

        <button
          type="button"
          style={{
            marginTop: 16,
            padding: '12px 16px',
            border: 0,
            borderRadius: 'var(--nx-r)',
            background: 'var(--nx-grad)',
            color: 'var(--nx-white)',
            fontFamily: 'var(--nx-ff-h)',
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            width: '100%',
          }}
          onClick={() => void login(returnTo)}
        >
          Sign in
        </button>

        {authMode === 'mock' && (
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                color: 'var(--nx-cyan)',
                fontFamily: 'var(--nx-ff-h)',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              Mock sign-in
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {users.map((u) => (
                <li key={u.id} style={{ marginBottom: 6 }}>
                  <button
                    type="button"
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: 'var(--nx-bg2)',
                      border: '1px solid var(--nx-border)',
                      color: 'var(--nx-text)',
                      borderRadius: 6,
                      padding: '8px 10px',
                      fontFamily: 'var(--nx-ff-b)',
                      fontSize: 13,
                    }}
                    onClick={() => {
                      const role = u.roles[0]!;
                      signInMockRole(role);
                      window.location.assign(returnTo);
                    }}
                  >
                    <div>{u.displayName}</div>
                    <div style={{ color: 'var(--nx-muted)', fontSize: 11 }}>{u.email}</div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}